import { useEffect, useRef, useState } from 'react';
import { getSimulationsData } from '../services/contentService';
import PremiumAccessDialog from './PremiumAccessDialog';
import {
  generateClientUUID,
  gradeSimulationAttempt,
  isUserConnected,
} from '../services/progressService';
import { submitProgressAttemptWithFallback } from '../services/progressSyncService';
import { readJsonArray, writeJson } from '../utils/storage';
import { isMultiAnswer, hasAnswer, toggleAnswer, isSelected,
  isAnswerCorrect, answerLabels, correctAnswerLabels, attemptAnswer } from '../utils/multiAnswer';
import {
  canGradeLocally,
  questionWithServerCorrection,
} from '../utils/serverGrading';

const Simulations = ({ onGoUpLevel, onGoBackTab, onOpenPremium }) => {
  const [simulationsData, setSimulationsData] = useState({});
  const [loadingContent, setLoadingContent] = useState(true);
  const [contentError, setContentError] = useState(null);
  const [lockedTitle, setLockedTitle] = useState(null);

  const [selectedTrack, setSelectedTrack] = useState(null);
  const [selectedLot, setSelectedLot] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [examFinished, setExamFinished] = useState(false);
  const [score, setScore] = useState(0);
  const [gradingResult, setGradingResult] = useState(null);
  const [gradingError, setGradingError] = useState(null);
  const [gradingInProgress, setGradingInProgress] = useState(false);
  const [correctionVisible, setCorrectionVisible] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [examMode, setExamMode] = useState(false);
  const [quizStarted, setQuizStarted] = useState(false);

  const timerRef = useRef(null);
  const attemptUuidRef = useRef(null);
  const startedAtRef = useRef(null);
  const finishGuardRef = useRef(false);
  const questionNavRef = useRef(null);

  const sessionActiveRef = useRef(false);
  sessionActiveRef.current = quizStarted;

  useEffect(() => {
    let isMounted = true;
    let requestVersion = 0;
    const load = () => {
      if (sessionActiveRef.current) return;
      const version = ++requestVersion;
      setContentError(null);
      setLoadingContent(true);
      getSimulationsData()
      .then((data) => {
        if (isMounted && version === requestVersion) {
          setSimulationsData(data || {});
          setLoadingContent(false);
        }
      })
      .catch((err) => {
        if (isMounted && version === requestVersion) {
          setContentError(err.message || 'Erreur lors du chargement des simulations');
          setLoadingContent(false);
        }
      });
    };
    const visible = () => { if (document.visibilityState === 'visible') load(); };
    load();
    window.addEventListener('focus', load);
    document.addEventListener('visibilitychange', visible);
    return () => {
      isMounted = false;
      window.removeEventListener('focus', load);
      document.removeEventListener('visibilitychange', visible);
    };
  }, []);

  const resetSession = () => {
    setCurrentQuestionIndex(0);
    setUserAnswers({});
    setExamFinished(false);
    setCorrectionVisible(false);
    setScore(0);
    setGradingResult(null);
    setGradingError(null);
    setGradingInProgress(false);
    setExamMode(false);
    setTimeRemaining(null);
    setQuizStarted(false);
    attemptUuidRef.current = null;
    startedAtRef.current = null;
    finishGuardRef.current = false;
  };

  useEffect(() => {
    if (!onGoUpLevel || !onGoBackTab) return;

    if (selectedSubject) {
      onGoUpLevel(() => {
        resetSession();
        setSelectedSubject(null);
      }, 3);
    } else if (selectedLot) {
      onGoUpLevel(() => {
        setSelectedLot(null);
      }, 2);
    } else if (selectedTrack) {
      onGoUpLevel(() => {
        setSelectedTrack(null);
      }, 1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTrack, selectedLot, selectedSubject, onGoUpLevel, onGoBackTab]);

  const selectedTrackData = selectedTrack ? simulationsData[selectedTrack] : null;
  const selectedLotData =
    selectedTrackData?.lots?.find((lot) => lot.key === selectedLot) || null;
  const selectedSubjectData =
    selectedLotData?.sujets?.find((subject) => subject.key === selectedSubject) || null;

  const selectedLotIndex =
    selectedTrackData?.lots?.findIndex((lot) => lot.key === selectedLot) ?? -1;
  const selectedSubjectIndex =
    selectedLotData?.sujets?.findIndex((subject) => subject.key === selectedSubject) ?? -1;
  const selectedLotLabel =
    selectedLotIndex >= 0 ? `Lot ${selectedLotIndex + 1}` : 'Lot';
  const selectedSubjectLabel =
    selectedSubjectIndex >= 0 ? `Sujet ${selectedSubjectIndex + 1}` : 'Sujet';

  const currentQuestions = selectedSubjectData?.questions || [];
  const examActive = currentQuestions.length > 0 && !examFinished && quizStarted;
  const answeredCount = currentQuestions.reduce(
    (count, question) => count + (hasAnswer(userAnswers[question.numero]) ? 1 : 0),
    0
  );
  const answeredProgress = currentQuestions.length > 0
    ? (answeredCount / currentQuestions.length) * 100
    : 0;

  useEffect(() => {
    if (examMode && timeRemaining !== null && timeRemaining > 0 && !examFinished && quizStarted) {
      timerRef.current = setTimeout(() => {
        setTimeRemaining((value) => value - 1);
      }, 1000);
    } else if (timeRemaining === 0 && examMode && !examFinished && quizStarted) {
      finishExam();
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRemaining, examMode, examFinished, quizStarted]);

  useEffect(() => {
    if (!quizStarted || examMode || !questionNavRef.current) return;

    const container = questionNavRef.current;
    const activeButton = container.querySelector(
      `[data-question-index="${currentQuestionIndex}"]`
    );

    if (!activeButton || typeof container.scrollTo !== 'function') return;

    const targetLeft =
      activeButton.offsetLeft - container.clientWidth / 2 + activeButton.clientWidth / 2;

    container.scrollTo({
      left: Math.max(0, targetLeft),
      behavior: 'smooth',
    });
  }, [currentQuestionIndex, quizStarted, examMode]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs
        .toString()
        .padStart(2, '0')}`;
    }

    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const selectTrack = (trackKey) => {
    resetSession();
    setSelectedTrack(trackKey);
    setSelectedLot(null);
    setSelectedSubject(null);
  };

  const selectLot = (lotKey) => {
    resetSession();
    setSelectedLot(lotKey);
    setSelectedSubject(null);
  };

  const selectSubject = (subjectKey) => {
    resetSession();
    setSelectedSubject(subjectKey);
  };

  const handleAnswerSelect = (questionNum, answerIndex) => {
    const question = currentQuestions.find((q) => q.numero === questionNum);
    setUserAnswers((previous) => ({
      ...previous,
      [questionNum]: toggleAnswer(previous[questionNum], answerIndex, isMultiAnswer(question)),
    }));
  };

  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((index) => index - 1);
    }
  };

  const handleJumpToQuestion = (index) => {
    setCurrentQuestionIndex(index);
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < currentQuestions.length - 1) {
      setCurrentQuestionIndex((index) => index + 1);
    } else {
      finishExam();
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const finishExam = async () => {
    if (finishGuardRef.current || !selectedSubjectData || currentQuestions.length === 0) {
      return;
    }

    finishGuardRef.current = true;
    setGradingError(null);
    setGradingInProgress(true);

    const completedAt = new Date();
    const startedAt = startedAtRef.current || completedAt.toISOString();
    const durationSeconds = Math.max(
      0,
      Math.round((completedAt.getTime() - new Date(startedAt).getTime()) / 1000)
    );
    const currentUserId =
      localStorage.getItem('concours_bf_current_user') || null;
    const answers = currentQuestions.map((question) =>
      attemptAnswer(question, userAnswers[question.numero])
    );
    const simulationId = selectedSubjectData.simulation_id;
    const payload = {
      client_uuid: attemptUuidRef.current,
      simulation_id: simulationId,
      epreuve_key: selectedSubjectData.source_epreuve_key || null,
      lot_key: selectedSubjectData.source_lot_key || selectedLot || null,
      sujet_key: selectedSubjectData.source_sujet_key || selectedSubject || null,
      context_titre: selectedSubjectLabel,
      lot_titre: selectedLotLabel,
      est_sujets_officiels:
        selectedSubjectData.est_sujets_officiels || false,
      exam_mode: examMode,
      seuil_admission: selectedSubjectData.seuil_admission ?? null,
      started_at: startedAt,
      completed_at: completedAt.toISOString(),
      duration_seconds: durationSeconds,
      answers,
    };

    try {
      let grading = null;
      const localGradeAvailable = canGradeLocally(currentQuestions);

      if (localGradeAvailable) {
        const correctedCount = currentQuestions.reduce(
          (count, question) =>
            count +
            (isAnswerCorrect(question, userAnswers[question.numero]) ? 1 : 0),
          0
        );
        grading = {
          score:
            Math.round((correctedCount / currentQuestions.length) * 20 * 10) /
            10,
          correctCount: correctedCount,
          totalQuestions: currentQuestions.length,
          correction: [],
        };
      }

      const hasDatabaseIds =
        Number.isInteger(simulationId) &&
        simulationId > 0 &&
        answers.every(
          (answer) => Number.isInteger(answer.question_id) && answer.question_id > 0
        );

      if (hasDatabaseIds && attemptUuidRef.current) {
        if (isUserConnected()) {
          const submitted = await submitProgressAttemptWithFallback(
            currentUserId,
            'simulation',
            payload
          );
          grading = submitted?.result?.grading || grading;

          if (submitted?.queued && !grading) {
            throw new Error(
              'La tentative a été conservée localement, mais une connexion est nécessaire pour obtenir le score et la correction.'
            );
          }
        } else if (!grading) {
          const response = await gradeSimulationAttempt(payload);
          grading = response?.grading || null;
        }
      }

      if (!grading) {
        throw new Error(
          'Impossible de vérifier les réponses pour le moment. Vérifiez votre connexion puis réessayez.'
        );
      }

      const finalScore = Number(grading.score);
      const correctCount = Number(grading.correctCount);

      setGradingResult(grading);
      setScore(Number.isFinite(finalScore) ? finalScore : 0);
      setExamFinished(true);
      setCorrectionVisible(false);

      const newHistoryEntry = {
        user_id: currentUserId,
        date: completedAt.toISOString(),
        client_uuid: attemptUuidRef.current,
        titre: selectedSubjectLabel,
        lot: selectedLotLabel,
        matiere: 'Simulation',
        simulation_type: selectedTrack,
        score: Number.isFinite(finalScore) ? finalScore : 0,
        totalQuestions:
          Number(grading.totalQuestions) || currentQuestions.length,
        correctAnswers: Number.isFinite(correctCount) ? correctCount : 0,
        examMode,
        est_sujets_officiels:
          selectedSubjectData.est_sujets_officiels || false,
      };

      const history = readJsonArray('simulation_history');
      writeJson('simulation_history', [newHistoryEntry, ...history]);
    } catch (error) {
      setGradingError(
        error?.message ||
          'Impossible de vérifier les réponses. Vérifiez votre connexion.'
      );
      finishGuardRef.current = false;
    } finally {
      setGradingInProgress(false);
    }
  };

  const handleRestartExam = () => {
    resetSession();
  };

  const handleStartExam = (isExamMode) => {
    if (!selectedSubjectData || currentQuestions.length === 0) return;

    attemptUuidRef.current = generateClientUUID();
    startedAtRef.current = new Date().toISOString();
    finishGuardRef.current = false;

    setExamMode(isExamMode);
    setQuizStarted(true);

    if (isExamMode) {
      const configuredDuration = Number(selectedSubjectData.duree_minutes);
      setTimeRemaining(
        Number.isFinite(configuredDuration) && configuredDuration > 0
          ? configuredDuration * 60
          : currentQuestions.length * 90
      );
    } else {
      setTimeRemaining(null);
    }
  };

  return (
    <div className={examActive ? 'exam-session cb-simulations-page' : 'space-y-6 cb-simulations-page'}>
      {lockedTitle && <PremiumAccessDialog contentTitle={lockedTitle}
        onClose={() => setLockedTitle(null)}
        onUpgrade={() => { setLockedTitle(null); onOpenPremium?.(); }} />}
      {loadingContent && (
        <div className="flex items-center justify-center p-12 text-secondary">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mr-3" />
          Chargement des simulations...
        </div>
      )}

      {contentError && (
        <div className="p-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-2xl">
          ⚠️ {contentError}
        </div>
      )}

      {gradingError && (
        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 rounded-2xl border border-amber-200 dark:border-amber-800">
          {gradingError}
        </div>
      )}

      {!loadingContent && !contentError && !selectedTrack && (
        <>
          <section className="cb-section-intro">
            <span className="cb-section-kicker">MISE EN SITUATION</span>
            <h1>Simulations</h1>
            <p>Sélectionnez un type de concours pour retrouver les sujets disponibles.</p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 cb-simulation-track-grid">
            <button
              type="button"
              onClick={() => selectTrack('direct')}
              className="surface-card border border-color rounded-2xl p-6 text-left hover:border-purple-400 hover:shadow-md transition-all cb-simulation-track-card"
            >
              <span className="text-3xl" aria-hidden="true">🎯</span>
              <h2 className="text-xl sm:text-2xl font-bold mt-3">Concours directs</h2>
              <p className="text-sm text-secondary mt-2">
                Accédez aux lots de sujets disponibles.
              </p>
              <p className="text-sm font-semibold text-purple-600 dark:text-purple-400 mt-4">
                {simulationsData.direct?.lots?.length || 0} lot
                {(simulationsData.direct?.lots?.length || 0) !== 1 ? 's' : ''} disponible
                {(simulationsData.direct?.lots?.length || 0) !== 1 ? 's' : ''}
              </p>
            </button>

            <button
              type="button"
              onClick={() => selectTrack('professional')}
              className="surface-card border border-color rounded-2xl p-6 text-left hover:border-purple-400 hover:shadow-md transition-all cb-simulation-track-card"
            >
              <span className="text-3xl" aria-hidden="true">💼</span>
              <h2 className="text-xl sm:text-2xl font-bold mt-3">Concours professionnels</h2>
              <p className="text-sm text-secondary mt-2">
                Cette rubrique est prévue dans l'architecture et sera structurée ultérieurement.
              </p>
              <p className="text-sm font-semibold text-secondary mt-4">
                Contenu à définir
              </p>
            </button>
          </div>
        </>
      )}

      {!loadingContent &&
        !contentError &&
        selectedTrack === 'professional' &&
        !selectedLot &&
        !selectedSubject && (
          <section className="surface-card border border-color rounded-2xl p-6">
            <p className="text-xs font-semibold uppercase tracking-wide text-purple-600 dark:text-purple-400">
              Simulations
            </p>
            <h1 className="text-2xl sm:text-3xl font-bold mt-1">
              Concours professionnels
            </h1>
            <div className="mt-5 rounded-xl border border-dashed border-color p-6 text-center">
              <h2 className="font-bold">Structure à définir</h2>
              <p className="text-sm text-secondary mt-2">
                Aucun parcours métier n'est imposé pour le moment. Cette rubrique sera complétée
                lorsque son organisation aura été validée.
              </p>
            </div>
          </section>
        )}

      {!loadingContent &&
        !contentError &&
        selectedTrack === 'direct' &&
        !selectedLot &&
        !selectedSubject && (
          <>
            <section>
              <p className="text-xs font-semibold uppercase tracking-wide text-purple-600 dark:text-purple-400">
                Concours directs
              </p>
              <h1 className="text-2xl sm:text-3xl font-bold text-blue-800 dark:text-blue-400 mt-1">
                Lots de sujets
              </h1>
              <p className="text-sm text-secondary mt-1">
                Le nombre de lots affichés dépend des données disponibles.
              </p>
            </section>

            {selectedTrackData?.lots?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 cb-simulation-lot-grid">
                {selectedTrackData.lots.map((lot, index) => (
                  <button
                    key={lot.key}
                    type="button"
                    onClick={() => selectLot(lot.key)}
                    className="surface-card border border-color rounded-2xl p-5 text-left hover:border-purple-400 hover:shadow-md transition-all cb-simulation-lot-card"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <h2 className="text-xl font-bold">Lot {index + 1}</h2>
                      <span className="shrink-0 px-2.5 py-1 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-semibold">
                        {lot.sujets?.length || 0} sujet
                        {(lot.sujets?.length || 0) !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="surface-card border border-dashed border-color rounded-2xl p-6 text-center text-secondary">
                Aucun lot disponible pour le moment.
              </div>
            )}
          </>
        )}

      {selectedTrack === 'direct' &&
        selectedLotData &&
        !selectedSubject && (
          <>
            <section>
              <p className="text-xs font-semibold uppercase tracking-wide text-purple-600 dark:text-purple-400">
                Concours directs
              </p>
              <h1 className="text-2xl sm:text-3xl font-bold text-blue-800 dark:text-blue-400 mt-1">
                {selectedLotLabel}
              </h1>
              <p className="text-sm text-secondary mt-1">
                Sélectionnez un sujet.
              </p>
            </section>

            {selectedLotData.sujets?.length > 0 ? (
              <div className="surface-card border border-color rounded-xl overflow-hidden divide-y divide-color cb-simulation-subject-list">
                {selectedLotData.sujets.map((subject, index) => (
                  <button
                    key={subject.key}
                    type="button"
                    onClick={() => {
                      if (subject.locked) setLockedTitle(`Sujet ${index + 1}`);
                      else selectSubject(subject.key);
                    }}
                    className="w-full px-3 py-2.5 sm:px-4 sm:py-3 text-left hover:bg-secondary transition-colors focus-visible:outline-purple-600 cb-simulation-subject-row"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <h2 className="text-sm sm:text-base font-bold">
                        Sujet {index + 1}
                      </h2>

                      <div className="flex items-center gap-2 text-[11px] sm:text-xs text-secondary shrink-0">
                        <span>
                          {subject.nombre_questions || subject.questions?.length || 0} questions
                        </span>
                        {subject.duree_minutes && (
                          <span>{subject.duree_minutes} min</span>
                        )}
                        {subject.locked && (
                          <span className="font-semibold text-purple-600 dark:text-purple-400">
                            🔒 Premium · Voir les formules
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="surface-card border border-dashed border-color rounded-2xl p-6 text-center text-secondary">
                Aucun sujet disponible dans ce lot.
              </div>
            )}
          </>
        )}

      {selectedSubjectData && !quizStarted && !examFinished && (
        <div className="surface-card rounded-2xl p-5 sm:p-7 shadow-sm border border-color cb-mode-picker">
          <div className="text-center mb-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-purple-600 dark:text-purple-400">
              {selectedLotLabel}
            </p>
            <h1 className="text-xl sm:text-2xl font-bold mt-1">
              {selectedSubjectLabel}
            </h1>
            <p className="text-sm text-secondary mt-2">
              {currentQuestions.length} questions
              {selectedSubjectData.duree_minutes
                ? ` · ${selectedSubjectData.duree_minutes} minutes`
                : ''}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => handleStartExam(false)}
              disabled={currentQuestions.length === 0}
              className="p-5 rounded-2xl border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 hover:border-green-500 transition-all text-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="text-3xl mb-2" aria-hidden="true">📚</div>
              <h2 className="text-lg font-bold text-green-700 dark:text-green-400">
                Mode entraînement
              </h2>
              <p className="text-sm text-secondary mt-2">
                Navigation libre entre les questions, sans limite de temps.
              </p>
            </button>

            <button
              type="button"
              onClick={() => handleStartExam(true)}
              disabled={currentQuestions.length === 0}
              className="p-5 rounded-2xl border-2 border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 hover:border-red-500 transition-all text-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="text-3xl mb-2" aria-hidden="true">⏱️</div>
              <h2 className="text-lg font-bold text-red-700 dark:text-red-400">
                Mode examen
              </h2>
              <p className="text-sm text-secondary mt-2">
                Conditions chronométrées et sans retour en arrière.
              </p>
            </button>
          </div>
        </div>
      )}

      {currentQuestions.length > 0 && !examFinished && quizStarted && (
        <>
          <div className="exam-status-panel surface-card rounded-xl p-3 sm:p-4 shadow-sm border border-color">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <h2 className="text-base sm:text-lg font-bold text-blue-800 dark:text-blue-400 leading-tight">
                  {selectedSubjectLabel}
                </h2>
                {examMode && (
                  <p className="text-xs text-red-500 mt-0.5">
                    ⏱️ Mode examen - Pas de retour en arrière
                  </p>
                )}
              </div>

              {examMode && timeRemaining !== null && (
                <div
                  className={`text-lg sm:text-xl whitespace-nowrap font-bold ${
                    timeRemaining < 300 ? 'text-red-500' : 'text-primary'
                  }`}
                >
                  ⏱️ {formatTime(timeRemaining)}
                </div>
              )}
            </div>

            <div
              className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-1"
              role="progressbar"
              aria-label="Progression des questions traitées"
              aria-valuemin="0"
              aria-valuemax={currentQuestions.length}
              aria-valuenow={answeredCount}
            >
              <div
                className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${answeredProgress}%` }}
              />
            </div>

            <div className="flex items-center justify-between gap-3 text-xs sm:text-sm text-secondary">
              <span>
                {answeredCount} / {currentQuestions.length} traitée
                {answeredCount !== 1 ? 's' : ''}
              </span>
              <span>
                Question {currentQuestionIndex + 1} / {currentQuestions.length}
              </span>
            </div>

            {!examMode && (
              <div
                ref={questionNavRef}
                className="flex flex-nowrap gap-1.5 mt-2 overflow-x-auto pb-1"
                aria-label="Navigation entre les questions"
              >
                {currentQuestions.map((question, index) => (
                  <button
                    key={question.numero}
                    type="button"
                    onClick={() => handleJumpToQuestion(index)}
                    data-question-index={index}
                    className={`w-7 h-7 shrink-0 rounded-full text-xs font-bold transition-all ${
                      currentQuestionIndex === index
                        ? 'bg-purple-600 text-white'
                        : hasAnswer(userAnswers[question.numero])
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-secondary'
                    }`}
                  >
                    {question.numero}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="exam-question-panel surface-card rounded-xl p-4 sm:p-5 shadow-sm border border-color">
            <div className="mb-3">
              <span className="inline-block px-2.5 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-full text-xs font-medium mb-2">
                Question {currentQuestions[currentQuestionIndex].numero}
              </span>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white leading-snug">
                {currentQuestions[currentQuestionIndex].question}
              </h3>
              {isMultiAnswer(currentQuestions[currentQuestionIndex]) && (
                <p className="mt-2 text-sm text-purple-700 dark:text-purple-300">
                  Plusieurs réponses sont correctes : cochez toutes les bonnes propositions.
                </p>
              )}
            </div>

            <div className="space-y-2 sm:space-y-3">
              {currentQuestions[currentQuestionIndex].options.map((option, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() =>
                    handleAnswerSelect(
                      currentQuestions[currentQuestionIndex].numero,
                      index
                    )
                  }
                  aria-label={`Réponse ${String.fromCharCode(65 + index)} : ${option}`}
                  aria-pressed={isSelected(userAnswers[currentQuestions[currentQuestionIndex].numero], index)}
                  className={`w-full px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg border-2 text-left transition-all min-h-11 ${
                    isSelected(userAnswers[currentQuestions[currentQuestionIndex].numero], index)
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400'
                      : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600 text-gray-900 dark:text-white'
                  }`}
                >
                  <span className="inline-flex w-7 h-7 shrink-0 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700 text-primary font-bold mr-3">
                    {isMultiAnswer(currentQuestions[currentQuestionIndex])
                      ? (isSelected(userAnswers[currentQuestions[currentQuestionIndex].numero], index) ? '☑' : '☐')
                      : String.fromCharCode(65 + index)}
                  </span>
                  {option}
                </button>
              ))}
            </div>
          </div>

          <div className="exam-actions flex justify-between gap-3">
            <button
              type="button"
              onClick={handlePrevQuestion}
              disabled={currentQuestionIndex === 0 || examMode}
              className={`px-4 sm:px-5 py-2.5 rounded-lg font-bold transition-all min-h-11 ${
                currentQuestionIndex === 0 || examMode
                  ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white'
              }`}
            >
              Précédent
            </button>

            <button
              type="button"
              onClick={handleNextQuestion}
              disabled={
                gradingInProgress ||
                !hasAnswer(userAnswers[currentQuestions[currentQuestionIndex].numero])
              }
              className={`px-4 sm:px-6 py-2.5 rounded-lg font-bold transition-all min-h-11 ${
                gradingInProgress ||
                !hasAnswer(userAnswers[currentQuestions[currentQuestionIndex].numero])
                  ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                  : 'bg-purple-600 hover:bg-purple-700 text-white shadow-lg hover:shadow-xl'
              }`}
            >
              {gradingInProgress
                ? 'Correction…'
                : currentQuestionIndex < currentQuestions.length - 1
                ? 'Suivant'
                : 'Terminer'}
            </button>
          </div>
        </>
      )}

      {examFinished && selectedSubjectData && currentQuestions.length > 0 && (
        <div className="surface-card rounded-2xl p-6 sm:p-8 shadow-lg border border-color cb-results-panel">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Examen terminé !
            </h2>

            <div className="inline-flex flex-col items-center justify-center w-32 h-32 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 text-white shadow-xl mb-4">
              <span className="text-4xl font-extrabold">{score}</span>
              <span className="text-sm">/ 20</span>
            </div>

            <p className="text-xl text-secondary">
              {score >= 16
                ? 'Excellent !'
                : score >= 12
                ? 'Très bien !'
                : score >= 8
                ? 'Bien !'
                : 'Continuez à pratiquer !'}
            </p>

            {examMode && (
              <p className="text-sm text-secondary mt-2">Mode examen</p>
            )}

            {Number.isFinite(Number(selectedSubjectData.seuil_admission)) &&
              Number(selectedSubjectData.seuil_admission) > 0 && (
                score >= Number(selectedSubjectData.seuil_admission) ? (
                  <p className="text-green-600 dark:text-green-400 font-bold mt-4">
                    ✓ Vous avez atteint le seuil d'admission (
                    {selectedSubjectData.seuil_admission}/20) !
                  </p>
                ) : (
                  <p className="text-red-600 dark:text-red-400 font-bold mt-4">
                    ✗ Seuil d'admission non atteint (
                    {selectedSubjectData.seuil_admission}/20 requis)
                  </p>
                )
              )}
          </div>

          {!correctionVisible ? (
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                type="button"
                onClick={handleRestartExam}
                className="px-8 py-4 bg-green-600 hover:bg-green-700 text-white text-lg font-bold rounded-xl transition-all shadow-lg hover:shadow-xl"
              >
                🔄 Refaire l'épreuve
              </button>
              <button
                type="button"
                onClick={() => setCorrectionVisible(true)}
                className="px-8 py-4 bg-purple-600 hover:bg-purple-700 text-white text-lg font-bold rounded-xl transition-all shadow-lg hover:shadow-xl"
              >
                👁️ Voir la correction
              </button>
            </div>
          ) : (
            <>
              <div className="space-y-4 mb-8">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  Détail de vos réponses
                </h3>

                {currentQuestions.map((originalQuestion) => {
                  const question = questionWithServerCorrection(
                    originalQuestion,
                    gradingResult
                  );
                  const isCorrect =
                    isAnswerCorrect(question, userAnswers[question.numero]);

                  return (
                    <div
                      key={question.numero}
                      className={`p-4 rounded-xl border ${
                        isCorrect
                          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                          : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <span className="text-sm font-bold">
                          {isCorrect ? 'Correct' : 'Incorrect'}
                        </span>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {question.numero}. {question.question}
                          </p>
                          <p className="text-sm mt-1">
                            <span className="text-secondary">Votre réponse : </span>
                            <span
                              className={
                                isCorrect
                                  ? 'text-green-600 dark:text-green-400'
                                  : 'text-red-600 dark:text-red-400'
                              }
                            >
                              {answerLabels(question, userAnswers[question.numero])}
                            </span>
                          </p>

                          {!isCorrect && (
                            <p className="text-sm mt-1">
                              <span className="text-green-600 dark:text-green-400">
                                Bonne réponse :{' '}
                              </span>
                              <span className="text-green-700 dark:text-green-500">
                                {correctAnswerLabels(question)}
                              </span>
                            </p>
                          )}

                          {question.explication && (
                            <p className="text-sm mt-2 text-secondary italic">
                              💡 {question.explication}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={handleRestartExam}
                  className="px-8 py-4 bg-purple-600 hover:bg-purple-700 text-white text-lg font-bold rounded-xl transition-all shadow-lg hover:shadow-xl"
                >
                  Refaire l'épreuve
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Simulations;
